import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { orgFileKeys } from "@/lib/query";

export type OrgFileSortField = "fileName" | "projectName" | "folderPath" | "createdAt";

export interface OrgFileFilters {
  search?: string;
  projectIds?: string[];
  folderPaths?: string[];
  sortBy?: OrgFileSortField;
  sortDir?: "asc" | "desc";
  limit?: number;
}

export interface OrgFileRow {
  id: string;
  fileName: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
  projectId: string | null;
  projectName: string | null;
  folderPath: string | null;
  uploadedByName: string | null;
}

interface OrgFilesPage {
  data: OrgFileRow[];
  nextCursor: { createdAt: string; id: string } | null;
  hasNext: boolean;
}

export interface OrgFolderAggregate {
  fullPath: string;
  fileCount: number;
  projectCount: number;
}

export interface OrgFolderDrilldownRow {
  id: string;
  fileName: string;
  mimeType: string | null;
  fileSizeBytes: number | null;
  createdAt: string;
  projectName: string | null;
}

function buildOrgFilesQs(
  filters: OrgFileFilters,
  cursor: { createdAt: string; id: string } | null,
): string {
  const sp = new URLSearchParams();
  if (filters.search) sp.set("search", filters.search);
  for (const p of filters.projectIds ?? []) sp.append("projectIds", p);
  for (const f of filters.folderPaths ?? []) sp.append("folderPaths", f);
  if (filters.sortBy) sp.set("sortBy", filters.sortBy);
  if (filters.sortDir) sp.set("sortDir", filters.sortDir);
  if (filters.limit) sp.set("limit", String(filters.limit));
  if (cursor) {
    sp.set("cursorCreatedAt", cursor.createdAt);
    sp.set("cursorId", cursor.id);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

export function useOrgFiles(filters: OrgFileFilters) {
  return useInfiniteQuery({
    queryKey: orgFileKeys.list(filters as Record<string, unknown>),
    initialPageParam: null as { createdAt: string; id: string } | null,
    queryFn: ({ pageParam }) =>
      apiFetch<OrgFilesPage>(`/org/files${buildOrgFilesQs(filters, pageParam)}`),
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.nextCursor : undefined),
  });
}

export function useOrgFolders() {
  return useQuery({
    queryKey: orgFileKeys.folders(),
    queryFn: () => apiFetch<OrgFolderAggregate[]>("/org/folders"),
  });
}

export function useOrgFolderDrilldown(folderPath: string, sortBy?: string, sortDir?: string) {
  const sp = new URLSearchParams({ folderPath });
  if (sortBy) sp.set("sortBy", sortBy);
  if (sortDir) sp.set("sortDir", sortDir);
  return useQuery({
    queryKey: orgFileKeys.folderDrilldown(folderPath, { sortBy, sortDir }),
    queryFn: () => apiFetch<OrgFolderDrilldownRow[]>(`/org/folders/files?${sp.toString()}`),
    enabled: !!folderPath,
  });
}
